import React, { useState, useEffect, useContext } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/authcontext";
import { ContractService } from "./contract.service";
import {
  showSuccessAlert,
  showErrorAlert,
} from "../../uiInterface/base-ui/alertservice";
import male from "../../../../src/assets/male.png";
import company from "../../../../src/assets/company.png";
import { formatCustomDateTime } from "../../../utils/dateUtils";
import { getStageColor } from "../../../utils/stageColors";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import axios from "axios";
import { API_BASE_URL } from "../../../environment";
import { getAuthHeaders } from "../../../utils/authUtils";
import ContractForm from "./contractForm";
import {
  useContractFormData,
  ContractFormDataProvider,
} from "./contractFormDataContext";
import Activities from "../../../components/Tabs/Activities";
import Notes from "../../../components/Tabs/Notes";
import Files from "../../../components/Tabs/Files";
import Tasks from "../../../components/Tabs/Tasks";
import Attachments from "../../../components/Tabs/Attachments";
import ProductsServices from "../../../components/Tabs/ProductsServices";
import Email from "../../../components/Tabs/Email";
import Chat from "../../../components/Tabs/Chat";
import Tabs from "../../../components/Tabs/tabs";
import { useTenantChange } from "../../../hooks/useTenantChange";
import { getStageColorByOrder } from "../../../utils/stageColors";
import { useEmailProviders } from "../../../hooks/useEmailProviders";
import ComposeEmail from "../../application/components/email/ComposeEmail";
import CustomFieldDisplay from "../../../components/CustomFieldDisplay";
import Tooltip from "@mui/material/Tooltip";
import SalesActivityModal from "../salesActivity/SalesActivityModal";
import {
  getLifecycleTypeName,
  getLifecycleTypePluralName,
} from "../../../utils/lifecycleUtils";
import { getApiErrorMessage } from "../../../utils/apiErrorUtils";

interface ContractDetailsContentProps {
  isEditContractOpen: boolean;
  setIsEditContractOpen: (value: boolean) => void;
}

const ContractDetailsContent = ({ isEditContractOpen, setIsEditContractOpen }: ContractDetailsContentProps) => {
  const route = all_routes;
  const { id } = useParams();
  const navigate = useNavigate();
  const { selectedTenantId } = useContext(AuthContext);
  const { contractTypes } = useContractFormData();
  const [contract, setContract] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [updateMessage, setUpdateMessage] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editError, setEditError] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>("");
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);
  const [activitiesRefreshTrigger, setActivitiesRefreshTrigger] = useState(0);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [showSalesActivityModal, setShowSalesActivityModal] = useState(false);

  const { hasConnectedProviders, loading: emailProvidersLoading } =
    useEmailProviders();
  const [isComposeEmailOpen, setIsComposeEmailOpen] = useState(false);

  const clearContractData = () => {
    setContract(null);
    setError(null);
    setLoading(true);
    setStagesLoading(true);
  };

  useTenantChange({
    onTenantChange: () => {
      clearContractData();
      navigate("/contracts");
    },
    clearData: clearContractData,
  });

  const createCompanyUrl = (contract: any): string => {
    if (!contract.company?.company_id) return "#";
    return `/companies/${contract.company.company_id}`;
  };

  const createContactUrl = (contract: any): string => {
    if (!contract.primary_contact?.contact_id) return "#";
    return `/contacts/${contract.primary_contact.contact_id}`;
  };

  const createProposalUrl = (contract: any): string => {
    if (!contract.proposal?.proposal_id) return "#";
    return `/proposals/${contract.proposal.proposal_id}`;
  };

  const handleStatusUpdate = async (typeId: number | null) => {
    if (!contract?.contract_id || !selectedTenantId) return;

    setUpdatingStatus(true);
    setUpdateMessage(null);

    try {
      await axios.patch(
        `${API_BASE_URL}/contract/${contract.contract_id}/`,
        { contract_type_id: typeId, tenant_id: selectedTenantId },
        { headers: getAuthHeaders() }
      );

      const refreshResponse = await axios.get(
        `${API_BASE_URL}/contract/${contract.contract_id}/?tenant_id=${selectedTenantId}`,
        { headers: getAuthHeaders() }
      );

      setContract(refreshResponse.data);
      setUpdateMessage(`${getLifecycleTypeName("CONTRACT")} type updated successfully!`);
      setActivitiesRefreshTrigger((prev) => prev + 1);
      setTimeout(() => setUpdateMessage(null), 3000);
    } catch (error) {
      console.error("Error updating contract type:", error);
      setUpdateMessage(`Failed to update ${getLifecycleTypeName("CONTRACT").toLowerCase()} type`);
      setTimeout(() => setUpdateMessage(null), 3000);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteContract = async () => {
    if (!contract?.contract_id || isDeleting || !selectedTenantId) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await ContractService.deleteContract(
        contract.contract_id,
        Number(selectedTenantId)
      );
      setOpenModal(false);
      setShowDeleteLoader(true);
      setTimeout(() => {
        setShowDeleteLoader(false);
        navigate(route.contracts);
      }, 1500);
    } catch (error: any) {
      console.error("Error deleting contract:", error);
      setDeleteError(
        error.response?.data?.message ||
        `Failed to delete ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`
      );
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteError("");
    setOpenModal(false);
  };

  const handleSendEmail = () => {
    setIsComposeEmailOpen(true);
  };

  const handleCloseComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const handleMinimizeComposeEmail = () => {
    setIsComposeEmailOpen(false);
  };

  const getPrimaryEmail = () => {
    if (contract?.primary_contact?.primary_email) {
      return contract.primary_contact.primary_email;
    }
    return "";
  };

  const isEmailEnabled =
    !emailProvidersLoading && hasConnectedProviders && getPrimaryEmail() !== "";

  const getEmailDisabledReason = () => {
    if (emailProvidersLoading) return "Checking email integration...";
    if (!hasConnectedProviders) return "Email is not integrated";
    if (!contract?.primary_contact) return `No contact attached to this ${getLifecycleTypeName("CONTRACT").toLowerCase()}`;
    if (!getPrimaryEmail()) return "Attached contact has no email address";
    return "Email is not available";
  };

  const isStageActive = (typeId: number): boolean => {
    if (!contract?.contract_type?.contract_type_id) return false;

    const currentTypeId = contract.contract_type.contract_type_id;
    const currentType = contractTypes.find(
      (type) => type.contract_type_id === currentTypeId
    );
    const checkType = contractTypes.find(
      (type) => type.contract_type_id === typeId
    );

    if (!currentType || !checkType) return false;

    return checkType.contract_type_order <= currentType.contract_type_order;
  };

  const handleEditSubmit = async (formData: any) => {
    if (!contract?.contract_id || isUpdating) return;

    setIsUpdating(true);
    setEditError("");

    try {
      const updatedContract = await ContractService.updateContract(
        contract.contract_id,
        formData as any
      );
      setContract(updatedContract);
      setActivitiesRefreshTrigger((prev) => prev + 1);

      closeOffcanvas("offcanvas_edit_contract");

      setTimeout(() => {
        showSuccessAlert(
          `${getLifecycleTypeName("CONTRACT")} "${updatedContract.subject}" updated successfully!`
        );
      }, 200);
    } catch (error: any) {
      console.error("Error updating contract:", error);
      setEditError(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      showErrorAlert(getApiErrorMessage(error, `Failed to update ${getLifecycleTypeName("CONTRACT").toLowerCase()}. Please try again.`));
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const closeOffcanvas = (offcanvasId: string) => {
    const offcanvas = document.getElementById(offcanvasId);
    if (offcanvas) {
      try {
        if ((window as any).bootstrap?.Offcanvas) {
          const bsOffcanvas = (window as any).bootstrap.Offcanvas.getInstance(
            offcanvas
          );
          if (bsOffcanvas) {
            bsOffcanvas.hide();
            return;
          }
        }

        const closeEvent = new Event("click");
        const closeButton = offcanvas.querySelector(
          '[data-bs-dismiss="offcanvas"]'
        );
        if (closeButton) {
          closeButton.dispatchEvent(closeEvent);
          return;
        }

        offcanvas.classList.remove("show");
        document.body.classList.remove("offcanvas-open");
        const backdrop = document.querySelector(".offcanvas-backdrop");
        if (backdrop) {
          backdrop.remove();
        }
      } catch (error) {
        console.warn("Error closing offcanvas:", error);
      }
    }
  };


  const DeletePreloader = () => (
    <div
      className="preloader"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <div className="loader"></div>
      <p className="mt-3 text-muted">
        {getLifecycleTypeName("CONTRACT")} deleted successfully. Redirecting...
      </p>
    </div>
  );

  useEffect(() => {
    const editOffcanvas = document.getElementById("offcanvas_edit_contract");

    const handleHidden = () => {
      setIsEditContractOpen(false);
    };

    if (editOffcanvas) {
      editOffcanvas.addEventListener("hidden.bs.offcanvas", handleHidden);
    }

    return () => {
      if (editOffcanvas) {
        editOffcanvas.removeEventListener("hidden.bs.offcanvas", handleHidden);
      }
    };
  }, []);


  useEffect(() => {
    const fetchContractDetails = async () => {
      if (!id || !selectedTenantId) {
        setError(`${getLifecycleTypeName("CONTRACT")} ID or Tenant ID is missing`);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const contractData = await ContractService.getContractById(Number(id));
        setContract(contractData);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching contract details:", err);
        if (err?.response?.status === 404) {
          navigate(route.contracts);
          return;
        }
        setError(err.message || `Failed to fetch ${getLifecycleTypeName("CONTRACT").toLowerCase()} details`);
      } finally {
        setLoading(false);
      }
    };

    fetchContractDetails();
  }, [id, selectedTenantId]);

  const refreshContractData = async () => {
    const contractId = id || contract?.contract_id;
    if (!contractId || !selectedTenantId) return;

    try {
      const contractData = await ContractService.getContractById(Number(contractId));
      setContract(contractData);
    } catch (err: any) {
      console.error("Error refreshing contract details:", err);
    }
  };

  useEffect(() => {
    if (contractTypes.length > 0 && stagesLoading) {
      setStagesLoading(false);
    }
  }, [contractTypes, stagesLoading]);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">{getLifecycleTypeName("CONTRACT")} Overview</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "400px" }}
                  >
                    <div className="text-center">
                      <div
                        className="spinner-border text-primary mb-3"
                        role="status"
                        style={{ width: "3rem", height: "3rem" }}
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <h5 className="text-muted">
                        Loading {getLifecycleTypeName("CONTRACT").toLowerCase()} details...
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !contract) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="alert alert-danger" role="alert">
                <h5 className="alert-heading">Error</h5>
                <p className="mb-0">{error || `${getLifecycleTypeName("CONTRACT")} not found`}</p>
                <hr />
                <Link to={route.contracts} className="btn btn-outline-danger">
                  <i className="ti ti-arrow-narrow-left me-1" />
                  Back to {getLifecycleTypePluralName("CONTRACT")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {showDeleteLoader && <DeletePreloader />}

      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <h4 className="page-title">{getLifecycleTypeName("CONTRACT")} Overview</h4>
                  </div>
                  <div className="col-sm-8 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {updateMessage && (
                <div className="row">
                  <div className="col-md-12">
                    <div
                      className={`alert ${updateMessage.includes("successfully")
                        ? "alert-success"
                        : "alert-danger"
                        } alert-dismissible fade show`}
                      role="alert"
                    >
                      {updateMessage}
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setUpdateMessage(null)}
                      ></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="contact-head">
                <div className="row align-items-center">
                  <div className="col-sm-6">
                    <ul className="contact-breadcrumb">
                      <li>
                        <Link to={route.contracts}>
                          <i className="ti ti-arrow-narrow-left" />
                          {getLifecycleTypePluralName("CONTRACT")}
                        </Link>
                      </li>
                      <li>{contract.subject && contract.subject.length > 70 ? (
                        <Tooltip title={contract.subject} placement="top" arrow>
                          <p className="mb-1" style={{ cursor: "default" }}>
                            {`${contract.subject.substring(0, 70)}...`}
                          </p>
                        </Tooltip>
                      ) : (
                        <p className="mb-1">{contract.subject}</p>
                      )}</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-body pb-2">
                  <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <div className="d-flex align-items-center mb-2">
                      <div
                        className="avatar avatar-xl me-3 flex-shrink-0 border p-2 bg-light"
                        style={{ borderRadius: "50%" }}
                      >
                        <i
                          className="ti ti-file-certificate"
                          style={{ color: "#824eeb", fontSize: "3rem" }}
                        />
                      </div>
                      <div>
                        <h3 className="mb-1">{contract.subject && contract.subject.length > 30 ? (
                          <Tooltip title={contract.subject} placement="top" arrow>
                            <p className="mb-1" style={{ cursor: "default" }}>
                              {`${contract.subject.substring(0, 30)}...`}
                            </p>
                          </Tooltip>
                        ) : (
                          <p className="mb-1">{contract.subject}</p>
                        )}</h3>
                        <p className="mb-1">
                          <i className="ti ti-building me-1" />
                          {contract.company?.company_name || "No Company"}
                        </p>
                      </div>
                    </div>
                    <div className="contacts-action">
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="mb-2">
                          <Select
                            options={contractTypes.map((type) => ({
                              value: type.contract_type_id,
                              label: type.contract_type_display_name,
                            }))}
                            value={
                              contractTypes.find(
                                (type) =>
                                  type.contract_type_id ===
                                  contract.contract_type?.contract_type_id
                              )
                                ? {
                                  value:
                                    contract.contract_type?.contract_type_id,
                                  label:
                                    contract.contract_type
                                      ?.contract_type_display_name,
                                }
                                : null
                            }
                            onChange={(option) =>
                              handleStatusUpdate(option?.value || null)
                            }
                            placeholder="Select type"
                            isClearable={false}
                            isLoading={updatingStatus}
                            isDisabled={updatingStatus}
                            className="min-w-200"
                            styles={{
                              control: (provided) => ({
                                ...provided,
                                minWidth: "200px",
                                backgroundColor: "#28a745",
                                borderColor: "#28a745",
                                color: "white",
                              }),
                              singleValue: (provided) => ({
                                ...provided,
                                color: "white",
                              }),
                              placeholder: (provided) => ({
                                ...provided,
                                color: "rgba(255, 255, 255, 0.8)",
                              }),
                              menu: (provided) => ({
                                ...provided,
                                zIndex: 9999,
                              }),
                            }}
                          />
                        </div>
                        {isEmailEnabled ? (
                          <button
                            className="btn btn-secondary d-flex align-items-center mb-2 me-2"
                            onClick={handleSendEmail}
                            title="Send Email"
                          >
                            <i className="ti ti-mail me-2" />
                            Send Email
                          </button>
                        ) : (
                          <Tooltip title={getEmailDisabledReason()} placement="top" arrow>
                            <span
                              className="btn btn-secondary d-flex align-items-center mb-2 me-2"
                              style={{ cursor: "not-allowed", opacity: 0.5 }}
                            >
                              <i className="ti ti-mail me-2" />
                              Send Email
                            </span>
                          </Tooltip>
                        )}
                        <Link
                          to="#"
                          className="btn-icon d-flex align-items-center justify-content-center mb-2"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_edit_contract"
                          title={`Edit ${getLifecycleTypeName("CONTRACT")}`}
                          onClick={() => setIsEditContractOpen(true)}
                        >
                          <i className="ti ti-edit-circle fs-16" />
                        </Link>
                        <Link
                          className="btn-icon d-flex align-items-center justify-content-center mb-2"
                          to="#"
                          onClick={(e) => { e.preventDefault(); setShowSalesActivityModal(true); }}
                          title="Log Activity"
                        >
                          <i className="ti ti-clipboard-list fs-16" />
                        </Link>
                        <div className="dropdown">
                          <Link
                            to="#"
                            className="btn-icon d-flex align-items-center justify-content-center mb-2"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            title="More Actions"
                          >
                            <i className="ti ti-dots-vertical fs-16" />
                          </Link>
                          <div className="dropdown-menu dropdown-menu-end dropup">

                            <Link
                              className="dropdown-item d-flex align-items-center"
                              to="#"
                              onClick={() => setOpenModal(true)}
                            >
                              <i className="ti ti-trash text-danger me-2" />
                              Delete
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-3 theiaStickySidebar">
              <div className="card">
                <div className="card-body p-3">
                  <h6 className="mb-3 fw-semibold">{getLifecycleTypePluralName("CONTRACT")} Information</h6>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Amount</span>
                      <span className="col-6">
                        {contract.grand_total
                          ? `${contract.currency_code || ""
                          } ${Number(contract.grand_total || 0).toFixed(4)}`
                          : "Not specified"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Due Date</span>
                      <span className="col-6">
                        {formatCustomDateTime(contract.due_datetime || null)}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Expiration Date</span>
                      <span className="col-6">
                        {formatCustomDateTime(
                          contract.expiration_datetime || null
                        )}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">{getLifecycleTypeName("CONTRACT")} Type</span>
                      <span className="col-6">
                        <span
                          className={`badge ${getStageColor(
                            contract.contract_type?.contract_type_display_name,
                            contract.contract_type?.contract_type_id,
                            contract.contract_type?.contract_type_order
                          )}`}
                        >
                          {contract.contract_type?.contract_type_display_name ||
                            "Not Set"}
                        </span>
                      </span>
                    </li>
                  </ul>
                  <CustomFieldDisplay
                    customFields={(contract as any)?.custom_fields}
                    showTitle={true}
                  />

                  <h6 className="mb-3 fw-semibold">Assigned Team</h6>
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="avatar avatar-md me-2 d-flex align-items-center justify-content-center bg-light"
                      style={{ width: 40, height: 40, borderRadius: "50%" }}
                    >
                      <i className="ti ti-users fs-24 text-primary" />
                    </div>
                    <div>
                      <span className="fw-semibold">
                        {contract.assigned_team?.team_name || "Not Assigned"}
                      </span>
                    </div>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Assigned User</h6>
                  <div className="d-flex align-items-center mb-3">
                    <span
                      className="avatar avatar-sm border rounded-circle p-1 me-2"
                      style={{ borderRadius: "50%" }}
                    >
                      <img
                        src={contract.assigned_user?.avatar_url || male}
                        alt="Image"
                        style={{
                          width: "25px",
                          height: "25px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    </span>
                    <p>{contract.assigned_user?.full_name ?? "Not Assigned"}</p>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Company</h6>
                  <div className="d-flex align-items-center">
                    <span
                      className="avatar avatar-sm border rounded-circle p-1 me-2"
                      style={{ borderRadius: "50%" }}
                    >
                      <img
                        src={contract.company?.avatar_url || company}
                        alt="Image"
                        style={{ borderRadius: "50%" }}
                      />
                    </span>
                    <div
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      <Link to={createCompanyUrl(contract)} target="_blank">
                        {contract.company?.company_name || "Not specified"}
                      </Link>
                    </div>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Contact</h6>
                  <div className="d-flex align-items-center mb-3">
                    <span
                      className="avatar avatar-sm border rounded-circle p-1 me-2"
                      style={{ borderRadius: "50%" }}
                    >
                      <img
                        src={contract.primary_contact?.avatar_url || male}
                        alt="Image"
                        style={{
                          width: "25px",
                          height: "25px",
                          borderRadius: "50%",
                          objectFit: "cover",
                        }}
                      />
                    </span>
                    <Link
                      to={createContactUrl(contract)}
                      target="_blank"
                      style={{ textDecoration: "underline" }}
                    >
                      {contract.primary_contact?.full_name || "Unassigned"}
                    </Link>
                  </div>
                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Primary Phone</span>
                      <span className="col-6">
                        {contract.primary_contact?.primary_phone || "Not specified"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Primary Email</span>
                      <span className="col-6">
                        {contract.primary_contact?.primary_email || "Not specified"}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Job Title</span>
                      <span className="col-6">
                        {contract.primary_contact?.job_title || "Not specified"}
                      </span>
                    </li>
                  </ul>
                  <hr />

                  <h6 className="mb-3 fw-semibold">Related {getLifecycleTypeName("PROPOSAL")}</h6>
                  <div className="d-flex align-items-center mb-3">
                    <div
                      className="avatar avatar-md flex-shrink-0 border p-2 bg-light"
                      style={{ borderRadius: "50%" }}
                    >
                      <i
                        className="ti ti-file-text mt-2"
                        style={{ color: "#824eeb", fontSize: "1.5rem" }}
                      />
                    </div>
                    <div
                      style={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                    >
                      <Link
                        to={createProposalUrl(contract)}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(
                            `/proposals/${contract.proposal?.proposal_id}`,
                            { state: { proposalData: contract.proposal } }
                          );
                        }}
                      >
                        {contract.proposal?.subject && contract.proposal?.subject.length > 35 ? (
                          <Tooltip title={contract.proposal?.subject} placement="top" arrow>
                            <span className="d-block mb-1" style={{ cursor: "pointer" }}>
                              {`${contract.proposal?.subject.substring(0, 35)}...`}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="d-block mb-1">{contract.proposal?.subject || `No Related ${getLifecycleTypeName("PROPOSAL")}`}</span>
                        )}
                      </Link>
                    </div>
                  </div>
                  <hr />

                  <h6 className="mb-3 fw-semibold">{getLifecycleTypeName("CONTRACT")} Description</h6>
                  <div className="contract-description mb-3">
                    <p className="text-muted mb-0">
                      {contract.description || "No description provided"}
                    </p>
                  </div>
                  <hr />

                  {/* Tags Section */}

                  <>
                    <h6 className="mb-3 fw-semibold">Tags</h6>
                    <ul className="social-info">
                      <li style={{ padding: '5px' }}>
                        {
                          contract?.tags?.insert && Array.isArray(contract.tags.insert) && contract.tags.insert.length > 0 ? (
                            contract.tags.insert.map((tag: any, index: number) => (
                              <span key={tag.entity_tag_id || index}
                                className="badge me-1"
                                style={{
                                  backgroundColor: '#824EEB',
                                  color: 'white'
                                }}>
                                {tag.entity_tag_name}
                              </span>
                            ))
                          ) : (
                            <span className="text-muted">No Tags Assigned</span>
                          )
                        }
                      </li>
                    </ul>
                  </>
                  <hr />

                  <ul>
                    <li className="row mb-3">
                      <span className="col-6">Created At</span>
                      <span className="col-6">
                        {formatCustomDateTime(contract.created_datetime)}
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Created By</span>
                      <span className="col-6 d-flex align-items-center">
                        <span className="avatar avatar-xs me-1">
                          <img
                            src={contract.created_by?.avatar_url || male}
                            alt="Image"
                            style={{
                              width: "25px",
                              height: "20px",
                              borderRadius: "50%",
                            }}
                          />
                        </span>
                        <span>
                          {contract.created_by
                            ? `${contract.created_by.full_name}`
                            : "No User Found"}
                        </span>
                      </span>
                    </li>
                    <li className="row mb-3">
                      <span className="col-6">Last Modified</span>
                      <span className="col-6">
                        {formatCustomDateTime(contract.updated_datetime)}
                      </span>
                    </li>
                    <li className="row mb-0">
                      <span className="col-6">Modified By</span>
                      <span className="col-6 d-flex align-items-center">
                        <span className="avatar avatar-xs me-1">
                          <img
                            src={contract.updated_by?.avatar_url || male}
                            alt="Image"
                            style={{
                              width: "25px",
                              height: "20px",
                              borderRadius: "50%",
                            }}
                          />
                        </span>
                        {contract.updated_by
                          ? `${contract.updated_by.full_name}`
                          : "No User Found"}
                      </span>
                    </li>
                  </ul>

                </div>
              </div>
            </div>

            <div className="col-xl-9">
              <div className="card mb-3">
                <div className="card-body pb-0">
                  <h4 className="fw-semibold mb-3">{getLifecycleTypeName("CONTRACT")} Pipeline Status</h4>
                  {stagesLoading ? (
                    <div className="d-flex align-items-center justify-content-center py-3">
                      <div
                        className="spinner-border spinner-border-sm text-primary me-2"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <span className="text-muted">
                        Loading pipeline stages...
                      </span>
                    </div>
                  ) : contractTypes.length > 0 ? (
                    <div className="pipeline-list">
                      <ul>
                        {contractTypes.map((type) => (
                          <li key={type.contract_type_id}>
                            <Link
                              to="#"
                              className={
                                isStageActive(type.contract_type_id)
                                  ? getStageColorByOrder(
                                    type.contract_type_order
                                  )
                                  : "bg-gray"
                              }
                              style={{
                                pointerEvents: isStageActive(
                                  type.contract_type_id
                                )
                                  ? "auto"
                                  : "none",
                                color: isStageActive(type.contract_type_id)
                                  ? ""
                                  : "#fff",
                              }}
                            >
                              {type.contract_type_display_name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <div className="text-center py-3">
                      <span className="text-muted">
                        No pipeline stages found
                      </span>
                    </div>
                  )}
                  <Tabs showEmailTab={true} isContractPage={true} showProductsServicesTab={true} showChatTab={true} />
                </div>
              </div>

              <div className="tab-content pt-0">
                <div className="tab-pane fade show active" id="activities">
                  <Activities
                    tenant_id={selectedTenantId}
                    related_entity_id={contract?.contract_id}
                    refreshTrigger={activitiesRefreshTrigger}
                    related_entity_name="contract"
                    crm_entity_id={contract?.crm_entity_id}
                  />
                </div>

                <Notes
                  tenant_id={selectedTenantId}
                  related_entity_id={contract?.contract_id}
                  related_entity_name="contract"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <Files
                  tenant_id={selectedTenantId}
                  related_entity_id={contract?.contract_id}
                  related_entity_name="contract"
                  refreshTrigger={activitiesRefreshTrigger}
                />

                <Tasks
                  tenant_id={selectedTenantId}
                  related_entity_id={contract?.contract_id}
                  related_entity_name="contract"
                  refreshTrigger={activitiesRefreshTrigger}
                />

                <Attachments
                  tenant_id={selectedTenantId}
                  related_entity_id={contract?.contract_id}
                  related_entity_name="contract"
                  refreshTrigger={activitiesRefreshTrigger}
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                />

                <ProductsServices
                  tenant_id={selectedTenantId}
                  related_entity_id={contract?.contract_id}
                  related_entity_name="contract"
                  onActivityUpdate={() =>
                    setActivitiesRefreshTrigger((prev) => prev + 1)
                  }
                  onEntityUpdate={refreshContractData}
                  currencyCode={contract?.currency_code}
                />

                <Email
                  contactIds={
                    contract?.primary_contact?.contact_id
                      ? [contract.primary_contact.contact_id]
                      : []
                  }
                />

                <Chat
                  tenant_id={selectedTenantId}
                  contact_id={contract?.primary_contact?.contact_id}
                  isContactPage={true}
                  lifecycleName="contract"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={openModal} onHide={() => setOpenModal(false)}>
        <div className="modal-body">
          <div className="text-center">
            <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
              <i className="ti ti-trash-x fs-36 text-danger" />
            </div>
            <h4 className="mb-2">Delete {getLifecycleTypeName("CONTRACT")}?</h4>
            <p className="mb-0">
              Are you sure you want to delete this {getLifecycleTypeName("CONTRACT").toLowerCase()}? This action cannot
              be undone.
            </p>
            {deleteError && (
              <div className="alert alert-danger mt-3" role="alert">
                {deleteError}
              </div>
            )}
            <div className="d-flex align-items-center justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-light me-2"
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteContract}
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <SalesActivityModal
        isVisible={showSalesActivityModal}
        onClose={() => setShowSalesActivityModal(false)}
        crmEntityId={contract?.crm_entity_id}
        recordId={contract?.contract_id}
        onSuccess={() => setActivitiesRefreshTrigger((prev) => prev + 1)}
      />

      <div
        className="offcanvas offcanvas-end"
        tabIndex={-1}
        id="offcanvas_edit_contract"
        style={{ width: "550px" }}

      >
        <div className="offcanvas-header border-bottom">
          <h5 className="fw-semibold">Edit {getLifecycleTypeName("CONTRACT")}</h5>
          <button
            type="button"
            className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          {editError && (
            <div className="alert alert-danger" role="alert">
              {editError}
            </div>
          )}
          {isEditContractOpen && (
            <ContractForm
              contract={contract}
              onSubmit={handleEditSubmit}
              onCancel={() => closeOffcanvas("offcanvas_edit_contract")}
              isMainApiCompleted={true}
              isSubmitting={isUpdating}
            />
          )}
        </div>
      </div>

      {/* Compose Email Modal */}
      <ComposeEmail
        isOpen={isComposeEmailOpen}
        onClose={handleCloseComposeEmail}
        onMinimize={handleMinimizeComposeEmail}
        draftData={{
          send_to: getPrimaryEmail(),
          subject: `Re: ${contract?.subject || getLifecycleTypeName("CONTRACT")}`,
        }}
        readOnlyRecipient={true}
      />
    </>
  );
};

const ContractDetails = () => {
  const { selectedTenantId } = useContext(AuthContext);
  const [isEditContractOpen, setIsEditContractOpen] = useState(false);

  return (
    <ContractFormDataProvider
      isMainApiCompleted={true}
      shouldLoad={isEditContractOpen}
      loadTypesOnly={true}
    >
      <ContractDetailsContent
        isEditContractOpen={isEditContractOpen}
        setIsEditContractOpen={setIsEditContractOpen}
      />
    </ContractFormDataProvider>
  );
};

export default ContractDetails;
